import { format } from 'date-fns';
import {
  APPLICATION_PATH,
  APPLY_PATH,
  BENEFITS_BASE_URL,
  BENEFIT_ELIGIBILITY_URL,
  BENEFIT_OVERVIEW_URL,
  CAREER_ACCELERATOR_URL,
  CATALOG_QUIZ_PATH,
  CATALOG_RECOMMENDATIONS_PATH,
  CATALOG_URL,
  DIRECT_PAYMENT_PATH,
  FIND_FUNDING_URL,
  GENESIS_URL,
  PARTIAL_FUNDING_PATH,
  PROFILE_COMPLETION_URL,
  PROGRAM_COMPLETION_DATES_PATH,
  PUBLIC_URL,
  SALESFORCE_NEW_REQUEST_URL,
  SPONSORSHIP_URL,
  TUITION_REIMBURSEMENT_URL,
  USER_EDIT_PATH,
} from '@src/constants';
import { makePossessive } from '@src/utilities/format';
import { MilestoneContent, MilestoneVariant } from './Milestone/Milestone';

export interface MilestoneConfig {
  useMemberTraits?: boolean;
  useUserFundingLimit?: boolean;
  useEmployerService?: boolean;
  useEmployerConfig?: boolean;
  useLearningLifecycleService?: boolean;
  content: (variables?: Record<string, any>) => MilestoneContent;
}

const preApplicantCatalog: MilestoneConfig = {
  content: () => {
    return {
      label: 'Anytime',
      title: 'Explore your learning options',
      body:
        "Guild's catalog features trusted schools with programs that fit into your life. Compare programs and bookmark your favorites.",
      actions: {
        primary: {
          text: 'Explore catalog',
          href: CATALOG_URL,
        },
        secondary: {
          text: 'Contact Guild Support',
          openInNewWindow: true,
          href: SALESFORCE_NEW_REQUEST_URL,
        },
      },
      variant: MilestoneVariant.Secondary,
    };
  },
};

const preApplicantCatalogWithRecs: MilestoneConfig = {
  content: () => {
    return {
      label: 'Next Step',
      title: 'Explore your learning options',
      body:
        "Guild's catalog features trusted schools with programs that fit into your life. Compare programs and bookmark your favorites.",
      actions: {
        primary: {
          text: 'Explore catalog',
          href: CATALOG_URL,
        },
        secondary: {
          text: 'View recommended programs',
          href: `${CATALOG_URL}/${CATALOG_RECOMMENDATIONS_PATH}`,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const preApplicantProgramQuiz: MilestoneConfig = {
  content: () => {
    return {
      label: 'Start Here',
      title: 'Take the program quiz',
      body:
        'There are so many learning programs to choose from! Finding the right one can take a lot of time and energy. Take our program quiz and get recommendations in minutes.',
      actions: {
        primary: {
          text: 'Take the quiz',
          href: `${CATALOG_URL}/${CATALOG_QUIZ_PATH}`,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const preApplicantRequestReimbursement: MilestoneConfig = {
  content: () => {
    return {
      label: 'Next Step',
      title: 'You may be eligible for reimbursement funding',
      body:
        "We'll check to make sure you meet all your employer's requirements, then you can apply to your selected education program.",
      actions: {
        primary: {
          text: 'Start your request',
          href: TUITION_REIMBURSEMENT_URL,
        },
        secondary: {
          text: 'How this works',
          href: `${BENEFITS_BASE_URL}/${PARTIAL_FUNDING_PATH}`,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const preApplicantRequestSponsorship: MilestoneConfig = {
  content: () => {
    return {
      label: 'Start Here',
      title: "You're eligible for the Direct Payment Program",
      body:
        "You'll request a Sponsorship letter and send it to your school. This lets your school know that your employer will pay some or all of your tuition. Then you can complete the application for your selected education program.",
      actions: {
        primary: {
          text: 'Visit Direct Payment',
          href: SPONSORSHIP_URL,
        },
        secondary: {
          text: 'How this works',
          href: `${BENEFITS_BASE_URL}/${DIRECT_PAYMENT_PATH}`,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const preApplicantFindFunding: MilestoneConfig = {
  content: () => {
    return {
      label: 'Anytime',
      title: 'Already have a school and program in mind?',
      body: "Check to see if it's covered and apply for funding today.",
      actions: {
        primary: {
          text: 'Check your benefit',
          href: FIND_FUNDING_URL,
        },
      },
      variant: MilestoneVariant.Secondary,
    };
  },
};

const transitionStartProgramApplication: MilestoneConfig = {
  useMemberTraits: true,
  content: (variables) => {
    const programId = variables?.transitionProgram?.programUuid || '';
    const programName = variables?.transitionProgram?.programName;
    const programNameString = programName ? `${programName} ` : '';
    const academicPartner = variables?.transitionProgram?.academicPartner;
    const academicPartnerString = academicPartner ? ` at ${academicPartner}` : '';

    return {
      label: 'Next Step',
      title: "Your program is in Guild's catalog",
      body: `Good news! You can get tuition paid directly to your school for your ${programNameString}program${academicPartnerString}. Start an application now, and be sure to check the deadline so you can stay enrolled without interruptions.`,
      actions: {
        primary: {
          text: 'Start a Guild application',
          href: `${GENESIS_URL}/${APPLY_PATH}/${programId}`,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const transitionRequestSponsorship: MilestoneConfig = {
  content: () => {
    return {
      label: 'Next Step',
      title: 'Request a Sponsorship letter',
      body:
        "Good news! Your program may be covered by the Direct Payment Program. You'll need to request a Sponsorship letter, which lets your school know that your employer will pay some or all of your tuition.",
      actions: {
        primary: {
          text: 'Visit Direct Payment',
          href: SPONSORSHIP_URL,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const transitionRequestReimbursement: MilestoneConfig = {
  useUserFundingLimit: true,
  content: (variables) => {
    let fundingLimitSentence = '';
    if (variables?.userFundingLimit) {
      fundingLimitSentence = `You could get paid back up to ${variables.userFundingLimit} each year for approved education expenses.`;
    }

    return {
      label: 'Next Step',
      title: 'See if you’re eligible for Tuition Reimbursement',
      body: `Based on the information you provided, you may be able to use the Tuition Reimbursement option. ${fundingLimitSentence}`,
      actions: {
        primary: {
          text: 'Start a request',
          href: TUITION_REIMBURSEMENT_URL,
        },
        secondary: {
          text: 'How this works',
          href: `${BENEFITS_BASE_URL}/${PARTIAL_FUNDING_PATH}`,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const preApplicantBachelorsWarning: MilestoneConfig = {
  content: () => {
    return {
      label: 'Start Here',
      title: 'You may not be covered',
      body:
        "If you already have a bachelor's degree, you're not eligible for Live Better U. If you completed a bachelor's degree in Puerto Rico or outside the U.S., you may still be covered.",
      actions: {
        primary: {
          text: 'Read benefit details',
          href: BENEFIT_OVERVIEW_URL,
        },
        secondary: {
          text: 'Contact Guild Support',
          openInNewWindow: true,
          href: SALESFORCE_NEW_REQUEST_URL,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const preApplicantNoPlanData: MilestoneConfig = {
  useEmployerService: true,
  content: (variables) => {
    return {
      label: 'Start Here',
      title: 'Learn more about your funding options',
      body: `We can't verify your account details right now, but try again later. Once you're verified you can explore all Guild has to offer. For now, check out ${makePossessive(
        variables?.employerName || 'your employer'
      )} benefit details or contact Guild Support.`,
      actions: {
        primary: {
          text: 'Explore benefit details',
          href: BENEFIT_ELIGIBILITY_URL,
        },
        secondary: {
          text: 'Contact Guild Support',
          openInNewWindow: true,
          href: SALESFORCE_NEW_REQUEST_URL,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const careerAccelerator: MilestoneConfig = {
  content: () => {
    return {
      label: 'Career prep',
      title: 'Boost your job-search skills',
      body:
        'Give your job search a boost with our collection of short lessons. Learn the tips and tricks, from resume writing to job hunting and beyond.',
      actions: {
        primary: {
          text: 'Go to Career Accelerator',
          href: CAREER_ACCELERATOR_URL,
        },
      },
      variant: MilestoneVariant.Secondary,
    };
  },
};

const completeYourSignup: MilestoneConfig = {
  content: () => {
    return {
      label: 'Start Here',
      title: 'Complete your signup',
      body:
        "Take a few moments to provide more information, then you'll be able to explore all Guild has to offer. Your profile information helps us get you the support you need to go to school and keep the tuition money flowing.",
      actions: {
        primary: {
          text: 'Complete your signup',
          href: `${PROFILE_COMPLETION_URL}/about_you`,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const completerExpectedCompletionDatePassed: MilestoneConfig = {
  useLearningLifecycleService: true,
  content: (variables) => {
    let expectedCompletionDate: string | undefined;
    if (variables?.currentLearningLifecycle?.expectedCompletionDate) {
      expectedCompletionDate = format(
        new Date(variables?.currentLearningLifecycle?.expectedCompletionDate),
        'MM/dd/yyyy'
      );
    }
    const programName = variables?.currentLearningLifecycle?.program?.name;
    const programNameString = programName ? ` ${programName}` : '';
    const academicPartner =
      variables?.currentLearningLifecycle?.program?.studentFacingAcademicPartner?.name;
    const completionDateDetailsString =
      programName && academicPartner && expectedCompletionDate
        ? ` of ${expectedCompletionDate} for ${programName} at ${academicPartner}`
        : '';
    return {
      label: 'Next step',
      title: `Did you finish your${programNameString} program?`,
      body: `The program completion date${completionDateDetailsString} you listed in your profile has passed. Take a minute to confirm your completion date.`,
      variant: MilestoneVariant.Primary,
    };
  },
};

const completerActualCompletionDateSaved: MilestoneConfig = {
  useLearningLifecycleService: true,
  content: (variables) => {
    let actualCompletionDate: string | undefined;
    if (variables?.currentLearningLifecycle?.actualCompletionDate) {
      actualCompletionDate = format(
        new Date(variables?.currentLearningLifecycle?.actualCompletionDate),
        'MM/dd/yyyy'
      );
    }
    const completionDateString = actualCompletionDate ? ` of ${actualCompletionDate}` : '';
    const programName = variables?.currentLearningLifecycle?.program?.name;
    const academicPartner =
      variables?.currentLearningLifecycle?.program?.studentFacingAcademicPartner?.name;
    const completionDetailsString =
      programName && academicPartner && actualCompletionDate
        ? `${programName} program at ${academicPartner} on ${actualCompletionDate}`
        : 'program';
    return {
      label: 'Next step',
      title: `Your completion date${completionDateString} is set`,
      body: `Congratulations on finishing your ${completionDetailsString}! Take a look at some other steps you can take now to get ahead.`,
      variant: MilestoneVariant.Primary,
    };
  },
};

// TODO: Rename this milestone to match the naming conventions (learnerSubmittedExpectedCompletionDate)
const submittedExpectedCompletionDate: MilestoneConfig = {
  useLearningLifecycleService: true,
  content: (variables) => {
    const programName = variables?.currentLearningLifecycle?.program?.name;
    const academicPartner =
      variables?.currentLearningLifecycle?.program?.studentFacingAcademicPartner?.name;
    const applicationDetailsString =
      academicPartner && programName ? `${programName} program at ${academicPartner}` : 'program';
    let expectedCompletionDate: string | undefined;
    if (variables?.currentLearningLifecycle?.expectedCompletionDate) {
      expectedCompletionDate = format(
        new Date(variables?.currentLearningLifecycle?.expectedCompletionDate),
        'MM/dd/yyyy'
      );
    }
    const completionDateString = expectedCompletionDate ? ` to ${expectedCompletionDate}` : '';

    return {
      label: 'completed',
      title: `You’ve set your completion date${completionDateString}`,
      body: `Thanks for letting us know about the end date for your ${applicationDetailsString}. If you need to update this or other program details, you can visit your profile any time.`,
      actions: {
        primary: {
          text: 'Visit your profile',
          href: `${PUBLIC_URL}${USER_EDIT_PATH}/#${PROGRAM_COMPLETION_DATES_PATH}`,
        },
      },
      variant: variables?.order === 1 ? MilestoneVariant.Primary : MilestoneVariant.Secondary,
    };
  },
};

const learnerSubmitExpectedCompletionDate: MilestoneConfig = {
  content: () => {
    return {
      label: 'suggested for you',
      title: 'Add your completion date',
      body:
        "Let us know when you expect to finish your program. It doesn't have to be the exact date. We'll use this information to highlight career and learning opportunities later.",
      variant: MilestoneVariant.Secondary,
    };
  },
};

// TO-DO: address when the BE is returning the correct information for the completion modal
// https://guild-education.atlassian.net/browse/HOME-1381
const learnerConfirmExpectedCompletionDate: MilestoneConfig = {
  content: () => {
    return {
      label: 'Next step',
      title: 'Add your completion date',
      body:
        "Let us know your estimated completion date by adding it to your profile. It doesn't have to be the exact date you'll finish your program, but if you're not sure, contact your academic advisor for details.",
      actions: {
        primary: {
          text: 'Update your profile',
          href: `${PUBLIC_URL}${USER_EDIT_PATH}/#${PROGRAM_COMPLETION_DATES_PATH}`,
        },
      },
      variant: MilestoneVariant.Secondary,
    };
  },
};

const learnerBooksAndFeesReimbursement: MilestoneConfig = {
  useEmployerConfig: true,
  content: (variables) => {
    let fullSentence = '';
    let startingSentence = '';
    let endingSentence = '';

    if (variables?.employerConfig) {
      const startingWindow = variables.employerConfig?.windowBegin?.daysAfter;
      const startingDays = variables.employerConfig?.windowBegin?.days;
      const endingWindow = variables.employerConfig?.windowEnd?.daysAfter;
      const endingDays = variables.employerConfig?.windowEnd?.days;
      if (startingWindow === 'term_start') {
        startingSentence =
          startingDays > 0
            ? `starting ${startingDays} days after your term start date`
            : 'starting after your term start date';
      } else {
        startingSentence =
          startingDays > 0
            ? `starting ${startingDays} days after your term end date`
            : 'starting after your term end date';
      }
      if (endingWindow === 'term_start') {
        endingSentence =
          startingDays > 0 ? ` and up to ${endingDays} days after the term start date` : '';
      } else {
        endingSentence =
          startingDays > 0
            ? ` and up to ${endingDays} days after the term end date`
            : ' and up to the term end date';
      }
      fullSentence = `Submit your expenses ${startingSentence}${endingSentence}.`;
    }

    return {
      label: 'Check the date',
      title: 'Get reimbursed for books & fees',
      body: `Get some money back! ${
        fullSentence || 'Submit a request for eligible expenses related to your program.'
      } Confirm those dates to make sure you get your request in on time. You don't want to lose any money!`,
      actions: {
        primary: {
          text: 'Manage reimbursements',
          href: TUITION_REIMBURSEMENT_URL,
        },
      },
      variant: MilestoneVariant.Secondary,
    };
  },
};

const learnerLearningPartnerTasks: MilestoneConfig = {
  useLearningLifecycleService: true,
  content: (variables) => {
    let schoolName = '';

    if (variables?.currentLearningLifecycle?.program?.studentFacingAcademicPartner) {
      const name = variables.currentLearningLifecycle.program.studentFacingAcademicPartner?.name;
      schoolName = name ? `${name}'s` : "your school's";
    }
    const programName = variables?.currentLearningLifecycle?.program?.name;
    const programNameString = programName ? ` ${programName}` : '';
    return {
      label: 'Start here',
      title: `Complete ${schoolName} process`,
      body: `Your Guild application has been approved! Review the steps ahead to start your${programNameString} program. If you've completed all the steps, move on to the next task.`,
      actions: {
        primary: {
          text: 'Review next steps',
          openInNewWindow: true,
          href: 'https://schools.guildeducation.com/',
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const applicantCreated: MilestoneConfig = {
  useLearningLifecycleService: true,
  content: (variables) => {
    const applicationId = variables?.currentLearningLifecycle?.applicationId || '';
    const programName = variables?.currentLearningLifecycle?.program?.name;
    const academicPartner =
      variables?.currentLearningLifecycle?.program?.studentFacingAcademicPartner?.name;
    const applicationDetailsString =
      programName && academicPartner ? ` for ${programName} at ${academicPartner}` : '';
    return {
      label: 'Next step',
      title: 'Complete your Guild application',
      body: `Get excited — you're about to unlock learning and career opportunities! Let us know if you need help completing your application${applicationDetailsString}. Too many Guild applications? Guild Support can clear those out for you.`,
      actions: {
        primary: {
          text: 'Go to application',
          href: `${GENESIS_URL}/${APPLICATION_PATH}/${applicationId}`,
        },
        secondary: {
          text: 'Contact Guild Support',
          openInNewWindow: true,
          href: SALESFORCE_NEW_REQUEST_URL,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const applicantSubmitted: MilestoneConfig = {
  useLearningLifecycleService: true,
  content: (variables) => {
    const applicationId = variables?.currentLearningLifecycle?.applicationId || '';
    const programName = variables?.currentLearningLifecycle?.program?.name;
    const academicPartner =
      variables?.currentLearningLifecycle?.program?.studentFacingAcademicPartner?.name;
    const applicationDetailsString =
      programName && academicPartner ? ` for ${programName} at ${academicPartner}` : '';
    return {
      label: 'Next step',
      title: 'Your Guild application has been submitted',
      body: `We've received your Guild application${applicationDetailsString} and will review it soon. Take a breather for now! If you like, you can review your submissions or reach out to Guild support with questions.`,
      actions: {
        primary: {
          text: 'Go to application',
          href: `${GENESIS_URL}/${APPLICATION_PATH}/${applicationId}`,
        },
        secondary: {
          text: 'Contact Guild Support',
          openInNewWindow: true,
          href: SALESFORCE_NEW_REQUEST_URL,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const applicantDeferred: MilestoneConfig = {
  useLearningLifecycleService: true,
  content: (variables) => {
    const applicationId = variables?.currentLearningLifecycle?.applicationId || '';
    return {
      label: 'Next step',
      title: 'Early application received',
      body:
        "The program you've applied for is full for now. But we'll review your early application for the next available term. Then we'll send you an update by email once the review is complete. Take a breather for now!",
      actions: {
        primary: {
          text: 'Go to application',
          href: `${GENESIS_URL}/${APPLICATION_PATH}/${applicationId}`,
        },
        secondary: {
          text: 'Contact Guild Support',
          openInNewWindow: true,
          href: SALESFORCE_NEW_REQUEST_URL,
        },
      },
      variant: MilestoneVariant.Primary,
    };
  },
};

const completerFindNextProgram: MilestoneConfig = {
  useEmployerService: true,
  content: (variables) => {
    return {
      label: 'Anytime',
      title: 'Find your next program',
      body: `Guild's catalog is full of learning programs that can help you go even further. Check ${makePossessive(
        variables?.employerName || 'your employer'
      )} policy to see if your next education journey is covered.`,
      actions: {
        primary: {
          text: 'View funding details',
          href: BENEFIT_OVERVIEW_URL,
        },
        secondary: {
          text: 'Explore catalog',
          href: CATALOG_URL,
        },
      },
      variant: variables?.order === 1 ? MilestoneVariant.Primary : MilestoneVariant.Secondary,
    };
  },
};

export default {
  applicantCreated,
  applicantDeferred,
  applicantSubmitted,
  careerAccelerator,
  completeYourSignup,
  completerExpectedCompletionDatePassed,
  completerFindNextProgram,
  completerActualCompletionDateSaved,
  learnerBooksAndFeesReimbursement,
  learnerConfirmExpectedCompletionDate,
  learnerLearningPartnerTasks,
  learnerSubmitExpectedCompletionDate,
  preApplicantBachelorsWarning,
  preApplicantCatalog,
  preApplicantCatalogWithRecs,
  preApplicantFindFunding,
  preApplicantNoPlanData,
  preApplicantProgramQuiz,
  preApplicantRequestReimbursement,
  preApplicantRequestSponsorship,
  submittedExpectedCompletionDate,
  transitionRequestReimbursement,
  transitionRequestSponsorship,
  transitionStartProgramApplication,
};
